/** @jsxImportSource react */
'use client';

import { track } from '@marketing/components/analytics';
import {
  openBeacon,
  openBeaconWithMessage,
} from '@marketing/components/beacon';
import { ArrowRight } from '@marketing/components/icons';

// Every lane opens the Help Scout Beacon. There is deliberately no email lane:
// publishing an address exposes it to scrapers, and Cloudflare Email Address
// Obfuscation would rewrite an email link into a /cdn-cgi/l/email-protection
// URL that 404s for any crawler that doesn't run JS.
type LaneAction =
  | { kind: 'beacon' }
  | { kind: 'beacon-message' }
  | { kind: 'beacon-bug' };

interface Lane {
  label: string;
  description: string;
  cta: string;
  action: LaneAction;
}

const LANES: Lane[] = [
  {
    action: { kind: 'beacon-message' },
    cta: 'Send us a message',
    description:
      'Anything pre-signup, partnership, or media. Real reply, usually same week.',
    label: 'General questions',
  },
  {
    action: { kind: 'beacon' },
    cta: 'Open a support ticket',
    description:
      'Already on OnArk? Open a ticket directly — it lands in our Help Scout inbox.',
    label: 'Customer support',
  },
  {
    action: { kind: 'beacon-bug' },
    cta: 'Report a bug',
    description:
      "We see these first. Tell us what broke and we'll pick it up same day if we can.",
    label: 'Bug reports',
  },
];

export function ContactLanes() {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
      {LANES.map(lane => (
        <LaneCard key={lane.label} lane={lane} />
      ))}
    </div>
  );
}

function LaneCard({ lane }: { lane: Lane }) {
  const handle = () => {
    track('contact_lane_clicked', { lane: lane.action.kind });
    if (lane.action.kind === 'beacon') {
      openBeacon();
    } else if (lane.action.kind === 'beacon-message') {
      openBeaconWithMessage('General question');
    } else {
      openBeaconWithMessage(
        'Bug report',
        'What were you doing when the bug happened?\n\nWhat did you expect to see?\n\nWhat did you see instead?\n\nBrowser + OS (e.g. Chrome on Mac):'
      );
    }
  };

  // A button, not an anchor — every lane is a JS action now, so an <a href="#">
  // would be a link that navigates nowhere for keyboard and crawler alike.
  return (
    <button
      className="group flex flex-col gap-4 rounded-[20px] bg-white p-9 text-left ring-1 ring-[color:var(--color-border-soft)] transition-all hover:-translate-y-1 hover:shadow-[0_24px_60px_-20px_rgba(15,11,26,0.15)]"
      type="button"
      onClick={handle}
    >
      <span className="text-[13px] font-medium uppercase tracking-[0.12em] text-[color:var(--color-amethyst)]">
        {lane.label}
      </span>
      <span className="text-[22px] font-semibold text-[color:var(--color-ink)] [letter-spacing:-0.01em]">
        {lane.cta}
      </span>
      <p className="text-[15px] leading-[1.6] text-[color:var(--color-gray-dark)]">
        {lane.description}
      </p>
      <span className="mt-auto inline-flex items-center gap-1.5 text-[14px] font-medium text-[color:var(--color-amethyst)]">
        {lane.action.kind === 'beacon'
          ? 'Open ticket form'
          : 'Open message form'}
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </span>
    </button>
  );
}
